import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from './SEO';

const HorseRidePicnic: React.FC = () => {
  const [activeTab, setActiveTab] = useState('romantic');

  const picnicOptions = [
    {
      id: 'romantic',
      title: 'Romantic Picnic',
      subtitle: 'For Couples',
      price: 'R2200',
      duration: 'Full Experience',
      description: 'Ride, dine, and unwind under pecan trees. Enjoy a scenic horseback ride, a romantic picnic with champagne, and peaceful horse-watching.',
      features: [
        'Scenic horseback ride',
        'Romantic picnic under pecan trees',
        'Champagne included',
        'Peaceful horse-watching',
        'Perfect for couples'
      ],
      image: '/romanticPicnic.jpg',
      color: 'from-pink-400 to-pink-600',
      bookingLink: 'https://secure.activitybridge.com/book?activityid=15941',
    },
    {
      id: 'everyday',
      title: 'Every Day Picnic',
      subtitle: 'For Families',
      price: 'R600/R800',
      duration: '30 minutes + picnic',
      description: '30-minute farm trail horse ride, followed by a picnic under the trees with treats for you and your horse.',
      features: [
        '30-minute farm trail horse ride',
        'Picnic under the trees',
        'Treats for you and your horse',
        'Perfect for families',
        'Beautiful countryside setting'
      ],
      image: '/picnic2.jpg',
      color: 'from-blue-400 to-blue-600',
      bookingLink: 'https://secure.activitybridge.com/book?activityid=15940',
    }
  ];

  const activePicnic = picnicOptions.find(picnic => picnic.id === activeTab);

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream-50 via-stables-50 to-earth-50">
      <SEO
        title="Horse Ride & Romantic Picnic Pretoria East | The Stables"
        description="Combine a scenic horse ride with a romantic picnic under the pecan trees. Perfect for couples and families. Book online from R600 per person at The Stables, Boschkop."
        path="/horse-ride-picnic"
        image="https://www.thestableshorserides.co.za/romanticPicnic.jpg"
      />
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-black bg-opacity-30"></div>
        <div className="relative z-10 text-center text-white px-4 max-w-5xl mx-auto">
          <h1 className="text-6xl md:text-8xl font-bold mb-6 animate-fade-in">
            Horse Ride & Picnic
          </h1>
          <p className="text-2xl md:text-3xl mb-8 font-light animate-fade-in-delay">
            Combine adventure with relaxation
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center animate-fade-in-delay-2">
            <a
              href="#picnics"
              className="bg-stables-600 hover:bg-stables-700 text-white px-6 py-3 rounded-lg text-base font-semibold transition-colors duration-300"
            >
              View Packages
            </a>
            <Link
              to="/horse-rides"
              className="bg-white text-stables-800 hover:bg-stables-100 px-6 py-3 rounded-lg text-base font-semibold transition-colors duration-300"
            >
              View Rides Only
            </Link>
          </div>
        </div>
        {/* Static Background */}
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{
          backgroundImage: "url('/picnic3.jpg')"
        }}></div>
        <div className="absolute inset-0 bg-black bg-opacity-30"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-transparent"></div>
      </section>

      {/* Interactive Picnic Selection */}
      <section id="picnics" className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          {/* Tab Navigation */}
          <div className="flex flex-wrap justify-center gap-4 mb-16">
            {picnicOptions.map((picnic) => (
              <button
                key={picnic.id}
                onClick={() => setActiveTab(picnic.id)}
                className={`px-6 py-3 rounded-lg font-semibold text-base transition-all duration-300 transform hover:scale-105 ${
                  activeTab === picnic.id
                    ? 'bg-stables-600 text-white shadow-2xl'
                    : 'bg-white text-stables-800 hover:bg-stables-100 shadow-lg'
                }`}
              >
                {picnic.title}
              </button>
            ))}
          </div>

          {/* Active Picnic Display */}
          {activePicnic && (
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="space-y-8">
                <div>
                  <h2 className="text-5xl font-bold text-stables-800 mb-2">
                    {activePicnic.title}
                  </h2>
                  <p className="text-2xl text-stables-600 mb-6">{activePicnic.subtitle}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-2 sm:gap-6">
                  <div className="text-center p-3 sm:p-6 bg-white rounded-2xl shadow-lg">
                    <div className="text-xl sm:text-3xl font-bold text-stables-600 leading-tight">{activePicnic.price}</div>
                    <div className="text-xs sm:text-sm text-stables-700 mt-1">per person</div>
                  </div>
                  <div className="text-center p-3 sm:p-6 bg-white rounded-2xl shadow-lg">
                    <div className="text-lg sm:text-3xl font-bold text-stables-600 leading-tight">{activePicnic.duration}</div>
                    <div className="text-xs sm:text-sm text-stables-700 mt-1">duration</div>
                  </div>
                </div>

                <p className="text-xl text-stables-700 leading-relaxed">
                  {activePicnic.description}
                </p>

                <div className="grid grid-cols-1 gap-4">
                  {activePicnic.features.map((feature, index) => (
                    <div key={index} className="flex items-center p-4 bg-white rounded-xl shadow-md">
                      <div className="w-3 h-3 bg-stables-600 rounded-full mr-3"></div>
                      <span className="text-stables-700 font-medium">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="pt-4">
                  <a
                    href={activePicnic.bookingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-stables-600 hover:bg-stables-700 text-white px-6 py-3 rounded-lg text-base font-semibold transition-colors duration-300"
                  >
                    Book This Experience
                  </a>
                </div>
              </div>

              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-stables-600 to-stables-700 rounded-3xl sm:rotate-3 sm:group-hover:rotate-6 transition-transform duration-300"></div>
                <img
                  src={activePicnic.image}
                  alt={activePicnic.title}
                  className="relative rounded-3xl shadow-2xl sm:-rotate-3 sm:group-hover:-rotate-6 transition-transform duration-300 w-full aspect-[4/3] object-cover"
                />
              </div>
            </div>
          )}
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-stables-800 mb-6">What's Included</h2>
            <p className="text-xl text-stables-700 max-w-3xl mx-auto">
              Our picnic experiences include everything you need for a perfect day out
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="group bg-white rounded-3xl p-8 shadow-xl border border-stables-200 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-gradient-to-r from-stables-600 to-stables-700 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-stables-800 mb-4">Guided Horse Ride</h3>
              <p className="text-stables-700 leading-relaxed">
                A scenic horse ride through our beautiful countryside with experienced guides 
                ensuring your safety and enjoyment.
              </p>
            </div>
            
            <div className="group bg-white rounded-3xl p-8 shadow-xl border border-stables-200 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-gradient-to-r from-stables-600 to-stables-700 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 01-2 2H9a2 2 0 01-2-2v-6m6 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v4.01" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-stables-800 mb-4">Delicious Food</h3>
              <p className="text-stables-700 leading-relaxed">
                Fresh, locally-sourced food and refreshments included in your picnic experience. 
                Special dietary requirements can be accommodated.
              </p>
            </div>
            
            <div className="group bg-white rounded-3xl p-8 shadow-xl border border-stables-200 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-gradient-to-r from-stables-600 to-stables-700 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-stables-800 mb-4">Beautiful Setting</h3>
              <p className="text-stables-700 leading-relaxed">
                Picnic under the shade of pecan trees with stunning countryside views 
                and peaceful surroundings perfect for relaxation.
              </p>
            </div>
            
            <div className="group bg-white rounded-3xl p-8 shadow-xl border border-stables-200 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-gradient-to-r from-stables-600 to-stables-700 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-stables-800 mb-4">All Equipment</h3>
              <p className="text-stables-700 leading-relaxed">
                We provide all necessary equipment including helmets, picnic blankets, 
                and everything you need for a comfortable experience.
              </p>
            </div>
            
            <div className="group bg-white rounded-3xl p-8 shadow-xl border border-stables-200 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-gradient-to-r from-stables-600 to-stables-700 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-stables-800 mb-4">Expert Staff</h3>
              <p className="text-stables-700 leading-relaxed">
                Our experienced and friendly staff ensure your safety and enjoyment 
                throughout your entire experience.
              </p>
            </div>
            
            <div className="group bg-white rounded-3xl p-8 shadow-xl border border-stables-200 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-gradient-to-r from-stables-600 to-stables-700 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-stables-800 mb-4">Photo Opportunities</h3>
              <p className="text-stables-700 leading-relaxed">
                Capture beautiful memories with our scenic backdrop and our staff 
                can help take photos of your group.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* All Picnic Options Overview */}
      <section className="py-20 bg-gradient-to-br from-cream-50 to-earth-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-stables-800 mb-6">All Our Picnic Experiences</h2>
            <p className="text-xl text-stables-700 max-w-3xl mx-auto">
              From romantic getaways to family adventures
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {picnicOptions.map((picnic) => (
              <div key={picnic.id} className="group relative">
                <div className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl"
                     style={{ background: `linear-gradient(135deg, ${picnic.color})` }}></div>
                <div className="relative bg-white rounded-3xl p-8 shadow-xl group-hover:shadow-2xl transition-all duration-300 transform group-hover:-translate-y-2">
                  <div className="text-center mb-6">
                    <h3 className="text-3xl font-bold text-stables-800 mb-2">{picnic.title}</h3>
                    <p className="text-stables-600 text-lg">{picnic.subtitle}</p>
                  </div>
                  
                  <div className="text-center mb-6">
                    <div className="text-4xl font-bold text-stables-600 mb-2">{picnic.price}</div>
                    <div className="text-sm text-stables-700">{picnic.duration}</div>
                  </div>

                  <p className="text-stables-700 mb-6 text-center">
                    {picnic.description}
                  </p>

                  <div className="space-y-3 mb-8">
                    {picnic.features.slice(0, 3).map((feature, index) => (
                      <div key={index} className="flex items-center">
                        <div className="w-2 h-2 bg-stables-600 rounded-full mr-3"></div>
                        <span className="text-sm text-stables-700">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <a
                    href={picnic.bookingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full block text-center bg-stables-600 hover:bg-stables-700 text-white py-4 rounded-xl font-semibold transition-colors duration-300"
                  >
                    Book Now
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced CTA Section */}
      <section className="py-20 bg-gradient-to-r from-stables-600 to-stables-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black bg-opacity-20"></div>
        <div className="relative z-10 max-w-5xl mx-auto text-center px-4">
          <h2 className="text-5xl font-bold mb-6">Ready for Your Picnic Adventure?</h2>
          <p className="text-2xl mb-12 max-w-3xl mx-auto">
            Book your horse ride and picnic experience today and create unforgettable memories with your loved ones.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <a
              href="#picnics"
              className="bg-white text-stables-800 hover:bg-stables-100 px-6 py-3 rounded-lg text-base font-semibold transition-colors duration-300"
            >
              Choose Your Package
            </a>
            <a
              href="tel:+27699464074"
              className="bg-stables-800 hover:bg-stables-900 text-white px-6 py-3 rounded-lg text-base font-semibold transition-colors duration-300"
            >
              Call Now
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HorseRidePicnic; 